import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'

import {
  Button,
  DragDropFileList,
  FileListFileProps,
  Input,
  Label,
  Modal,
  Select,
  Text,
  WarningOutlineIcon,
} from '@mercai/clever'

import {
  api,
  ORDER_CENTER_SHIPMENT_URL,
  QUERIES_ADMIN,
  useOrderCenterCommentsCreate,
} from '@/services'
import {
  getFileNameAndExtension,
  onErrorMessage,
  useValidationErrors,
} from '@/helpers'
import { schemaStep2 } from './modal-delivery-proof-warning.constants'

import { SaleOrderCommentTypesEnum } from '@/types'
import { ModalDeliveryProofProps } from './modal-delivery-proof-warning.type'
import styles from './modal-delivery-proof-warning.module.css'

export const ModalDeliveryProof = ({
  saleOrderUuid = '',
  isOpen,
  shipment,
  onSaveAfterUpload,
  onClose,
}: ModalDeliveryProofProps) => {
  const { t } = useTranslation('componentCardsOrderCenterShow')
  const queryClient = useQueryClient()

  const [step, setStep] = useState(0)
  const [step2Values, setStep2Values] = useState({
    reason: '',
    comment: '',
  })

  const [dataFilesProofOfDelivery, setDataFilesProofOfDelivery] = useState<
    FileListFileProps[]
  >([])

  const [fileUploadedSuccessfully, setFileUploadedSuccessfully] = useState({
    proofOfDelivery: !!shipment?.proofOfDelivery,
  })

  const { mutateAsync, isPending } = useOrderCenterCommentsCreate(saleOrderUuid)

  const { isValid, errors, onHandleListenErrors } = useValidationErrors(
    schemaStep2(t),
    step2Values,
  )

  const onSubmit = async () => {
    const response = await mutateAsync({
      type: SaleOrderCommentTypesEnum.proof_of_delivery,
      subject: step2Values.reason,
      comment: step2Values.comment,
    })

    if (response.success) {
      queryClient.invalidateQueries({
        queryKey: [QUERIES_ADMIN.ORDER_CENTER.module, saleOrderUuid],
      })

      onSaveAfterUpload()

      return
    }

    onErrorMessage(
      t('orderStatusesModalWarningSubmitError'),
      t('orderStatusesModalWarningSubmitErrorDescription'),
    )
  }

  const handleClickSubmit = () => {
    if (!isValid) {
      onHandleListenErrors(true)
      return
    }

    onHandleListenErrors(false)
    onSubmit()
  }

  const handleClickAdvanceStep = () => {
    if (fileUploadedSuccessfully.proofOfDelivery) {
      onSaveAfterUpload()
      return
    }

    setStep(1)
  }

  const handleClose = () => {
    setStep(0)
    onClose()
  }

  const steps = [
    {
      title: (
        <div className={styles['modal-header']}>
          <WarningOutlineIcon className={styles['header-icon']} />
          <Text variant="text-large-bold" className={styles.title}>
            <span>{t('orderStatusesModalDeliveryProofTitle')}</span>
          </Text>
        </div>
      ),
      description: t('orderStatusesModalDeliveryProofDescription'),
      component: (
        <div className={styles.component}>
          <div>
            <Label>{t('orderStatusesModalDeliveryProofFileLabel')}</Label>

            <DragDropFileList
              urlAdd={ORDER_CENTER_SHIPMENT_URL(shipment?.uuid || '')}
              configsRequest={{
                fileField: 'proof_of_delivery',
                method: 'PATCH',
              }}
              axiosInstance={api}
              data={dataFilesProofOfDelivery}
              onChangeData={setDataFilesProofOfDelivery}
              successedUpload={() =>
                setFileUploadedSuccessfully((prev) => ({
                  ...prev,
                  proofOfDelivery: true,
                }))
              }
              translations={{
                clickToUpload: t('uploadButton'),
                dragFile: t('uploadDrag'),
                sizeFile: t('uploadSize'),
              }}
            />
          </div>

          <div className={styles.footer}>
            <Button variant="secondary" onClick={handleClose}>
              {t('orderStatusesModalDeliveryProofCancelButton')}
            </Button>

            <Button variant="primary" onClick={handleClickAdvanceStep}>
              {fileUploadedSuccessfully.proofOfDelivery
                ? t('orderStatusesModalDeliveryProofSaveButton')
                : t('orderStatusesModalDeliveryProofSkipButton')}
            </Button>
          </div>
        </div>
      ),
    },
    {
      title: (
        <div className={styles['modal-header']}>
          <WarningOutlineIcon className={styles['header-icon']} />
          <Text variant="text-large-bold" className={styles.title}>
            <span>{t('orderStatusesModalDeliveryProofStep2Title')}</span>
          </Text>
        </div>
      ),
      description: t('orderStatusesModalDeliveryProofStep2Description'),
      component: (
        <div className={styles.component}>
          <div>
            <Label required>
              {t('orderStatusesModalDeliveryProofReasonLabel')}
            </Label>
            <Select
              placeholder={t(
                'orderStatusesModalDeliveryProofReasonPlaceholder',
              )}
              options={[
                {
                  label: t('orderStatusesModalDeliveryProofReasonOption1'),
                  value: t('orderStatusesModalDeliveryProofReasonOption1'),
                },
                {
                  label: t('orderStatusesModalDeliveryProofReasonOption2'),
                  value: t('orderStatusesModalDeliveryProofReasonOption2'),
                },
                {
                  label: t('orderStatusesModalDeliveryProofReasonOption3'),
                  value: t('orderStatusesModalDeliveryProofReasonOption3'),
                },
              ]}
              value={step2Values.reason}
              onChange={(reason) =>
                setStep2Values((prev) => ({ ...prev, reason }))
              }
              error={errors?.reason as string}
            />
          </div>

          <div>
            <Label>{t('orderStatusesModalDeliveryProofCommentLabel')}</Label>
            <Input
              isTextArea={true}
              value={step2Values.comment}
              onChange={(comment) =>
                setStep2Values((prev) => ({ ...prev, comment }))
              }
              error={errors?.comment as string}
            />
          </div>

          <div className={styles.footer}>
            <Button variant="secondary" onClick={handleClose}>
              {t('orderStatusesModalDeliveryProofCancelButton')}
            </Button>

            <Button
              variant="primary"
              onClick={handleClickSubmit}
              isActive={!isPending}
              disabled={isPending}
            >
              {t('orderStatusesModalDeliveryProofSaveButton')}
            </Button>
          </div>
        </div>
      ),
    },
  ]

  useEffect(() => {
    if (shipment?.proofOfDelivery) {
      const file = getFileNameAndExtension(shipment?.proofOfDelivery || '')

      setDataFilesProofOfDelivery([
        {
          id: file.fileName,
          name: `${file.fileName}.${file.extension}`,
          url: shipment?.proofOfDelivery,
          uploaded: true,
        },
      ])
    } else {
      setDataFilesProofOfDelivery([])
    }
  }, [shipment])

  return (
    <Modal.Root isOpen={isOpen} onHandleOpen={handleClose} size="small">
      <Modal.Header
        title={steps?.[step].title || ''}
        description={steps?.[step].description || ''}
        className={styles.header}
      />
      <Modal.Body className={styles.body}>{steps?.[step].component}</Modal.Body>
    </Modal.Root>
  )
}
